<template>
  <div>
    <!-- Begin page -->
    <div id="layout-wrapper">
      <NavigationBar />
      <!-- ============================================================== -->
      <!-- Start right Content here -->
      <!-- ============================================================== -->
      <div class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">
            <!-- start page title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Change Password</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript:void(0);">Home</a></li>
                      <li class="breadcrumb-item"><a href="javascript:void(0);">Settings</a></li>
                      <li class="breadcrumb-item active">Change Password</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
            <!-- end page title -->

            <div class="row justify-content-center">
              <div class="col-xl-8 col-lg-10">
                <!-- Security Info Card -->
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center mb-3">
                      <div class="avatar-sm me-3">
                        <div class="avatar-title bg-info bg-gradient rounded-circle text-white font-size-20">
                          <i class="mdi mdi-shield-lock"></i>
                        </div>
                      </div>
                      <div>
                        <h5 class="card-title mb-0">Password Security</h5>
                        <p class="text-muted mb-0">Keep your account secure by using a strong password</p>
                      </div>
                    </div>

                    <!-- Password Strength Indicators -->
                    <div class="alert alert-info border-0" role="alert">
                      <div class="d-flex">
                        <div class="flex-shrink-0">
                          <i class="mdi mdi-information-outline me-2 font-size-18"></i>
                        </div>
                        <div class="flex-grow-1">
                          <strong>Password Requirements:</strong>
                          <ul class="mb-0 mt-2">
                            <li>Minimum 8 characters long</li>
                            <li>Include uppercase and lowercase letters</li>
                            <li>Include at least one number</li>
                            <li>Include at least one special character (!@#$%^&*)</li>
                            <li>Password expires every 45 days</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <!-- User Info Display -->
                    <div class="row mb-4">
                      <div class="col-md-6">
                        <div class="d-flex align-items-center p-3 bg-light rounded">
                          <i class="mdi mdi-account-circle text-primary font-size-24 me-3"></i>
                          <div>
                            <small class="text-muted">Logged in as</small>
                            <p class="mb-0 fw-bold">{{ userInfo.names }}</p>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="d-flex align-items-center p-3 bg-light rounded">
                          <i class="mdi mdi-calendar-clock text-warning font-size-24 me-3"></i>
                          <div>
                            <small class="text-muted">Password Expiry</small>
                            <p class="mb-0 fw-bold">{{ passwordExpiryDisplay }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Change Password Form -->
                <div class="card">
                  <div class="card-header bg-gradient-primary">
                    <h5 class="card-title text-white mb-0">
                      <i class="mdi mdi-lock-reset me-2"></i>Update Your Password
                    </h5>
                  </div>
                  <div class="card-body">
                    <form @submit.prevent="changePassword">
                      <!-- Current Password -->
                      <div class="mb-4">
                        <label for="currentPassword" class="form-label">
                          Current Password <span class="text-danger">*</span>
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-lock"></i></span>
                          <input
                              v-model="formData.currentPassword"
                              :type="showCurrentPassword ? 'text' : 'password'"
                              class="form-control"
                              :class="{ 'is-invalid': errors.currentPassword }"
                              id="currentPassword"
                              placeholder="Enter your current password"
                              required
                          />
                          <button
                              class="btn btn-outline-secondary"
                              type="button"
                              @click="showCurrentPassword = !showCurrentPassword"
                          >
                            <i :class="showCurrentPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                          </button>
                        </div>
                        <div v-if="errors.currentPassword" class="invalid-feedback d-block">
                          {{ errors.currentPassword }}
                        </div>
                      </div>

                      <!-- New Password -->
                      <div class="mb-4">
                        <label for="newPassword" class="form-label">
                          New Password <span class="text-danger">*</span>
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-lock-plus"></i></span>
                          <input
                              v-model="formData.newPassword"
                              :type="showNewPassword ? 'text' : 'password'"
                              class="form-control"
                              :class="{ 'is-invalid': errors.newPassword }"
                              id="newPassword"
                              placeholder="Enter your new password"
                              required
                              @input="validatePassword"
                          />
                          <button
                              class="btn btn-outline-secondary"
                              type="button"
                              @click="showNewPassword = !showNewPassword"
                          >
                            <i :class="showNewPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                          </button>
                        </div>
                        <div v-if="errors.newPassword" class="invalid-feedback d-block">
                          {{ errors.newPassword }}
                        </div>

                        <!-- Password Strength Meter -->
                        <div v-if="formData.newPassword" class="mt-2">
                          <div class="d-flex justify-content-between align-items-center mb-1">
                            <small class="text-muted">Password Strength:</small>
                            <small :class="passwordStrengthClass">{{ passwordStrengthText }}</small>
                          </div>
                          <div class="progress" style="height: 5px;">
                            <div
                                class="progress-bar"
                                :class="passwordStrengthClass"
                                :style="{ width: passwordStrengthPercentage + '%' }"
                            ></div>
                          </div>

                          <!-- Password Requirements Checklist -->
                          <div class="mt-2">
                            <small class="d-block" :class="passwordChecks.length ? 'text-success' : 'text-muted'">
                              <i :class="passwordChecks.length ? 'mdi mdi-check-circle' : 'mdi mdi-circle-outline'"></i>
                              At least 8 characters
                            </small>
                            <small class="d-block" :class="passwordChecks.uppercase ? 'text-success' : 'text-muted'">
                              <i :class="passwordChecks.uppercase ? 'mdi mdi-check-circle' : 'mdi mdi-circle-outline'"></i>
                              Contains uppercase letter
                            </small>
                            <small class="d-block" :class="passwordChecks.lowercase ? 'text-success' : 'text-muted'">
                              <i :class="passwordChecks.lowercase ? 'mdi mdi-check-circle' : 'mdi mdi-circle-outline'"></i>
                              Contains lowercase letter
                            </small>
                            <small class="d-block" :class="passwordChecks.number ? 'text-success' : 'text-muted'">
                              <i :class="passwordChecks.number ? 'mdi mdi-check-circle' : 'mdi mdi-circle-outline'"></i>
                              Contains number
                            </small>
                            <small class="d-block" :class="passwordChecks.special ? 'text-success' : 'text-muted'">
                              <i :class="passwordChecks.special ? 'mdi mdi-check-circle' : 'mdi mdi-circle-outline'"></i>
                              Contains special character
                            </small>
                          </div>
                        </div>
                      </div>

                      <!-- Confirm New Password -->
                      <div class="mb-4">
                        <label for="confirmPassword" class="form-label">
                          Confirm New Password <span class="text-danger">*</span>
                        </label>
                        <div class="input-group">
                          <span class="input-group-text"><i class="mdi mdi-lock-check"></i></span>
                          <input
                              v-model="formData.confirmPassword"
                              :type="showConfirmPassword ? 'text' : 'password'"
                              class="form-control"
                              :class="{ 'is-invalid': errors.confirmPassword }"
                              id="confirmPassword"
                              placeholder="Re-enter your new password"
                              required
                              @input="validateConfirmPassword"
                          />
                          <button
                              class="btn btn-outline-secondary"
                              type="button"
                              @click="showConfirmPassword = !showConfirmPassword"
                          >
                            <i :class="showConfirmPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                          </button>
                        </div>
                        <div v-if="errors.confirmPassword" class="invalid-feedback d-block">
                          {{ errors.confirmPassword }}
                        </div>
                        <div v-else-if="formData.confirmPassword && formData.newPassword === formData.confirmPassword" class="text-success mt-1">
                          <i class="mdi mdi-check-circle"></i> Passwords match
                        </div>
                      </div>

                      <!-- Security Tips -->
                      <div class="alert alert-warning border-0 mb-4" role="alert">
                        <div class="d-flex">
                          <div class="flex-shrink-0">
                            <i class="mdi mdi-alert-circle-outline me-2 font-size-18"></i>
                          </div>
                          <div class="flex-grow-1">
                            <strong>Security Tips:</strong>
                            <ul class="mb-0 mt-2">
                              <li>Don't use personal information in your password</li>
                              <li>Don't reuse passwords from other accounts</li>
                              <li>Don't share your password with anyone</li>
                              <li>Change your password regularly</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <!-- Form Actions -->
                      <div class="d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            class="btn btn-light"
                            @click="resetForm"
                            :disabled="loading"
                        >
                          <i class="mdi mdi-refresh me-1"></i>Reset Form
                        </button>
                        <button
                            type="submit"
                            class="btn btn-primary"
                            :disabled="loading || !isFormValid"
                        >
                          <span v-if="loading">
                            <span class="spinner-border spinner-border-sm me-1"></span>
                            Changing Password...
                          </span>
                          <span v-else>
                            <i class="mdi mdi-content-save me-1"></i>Change Password
                          </span>
                        </button>
                      </div>
                    </form>
                  </div>
                </div>

                <!-- Additional Security Options -->
                <div class="card">
                  <div class="card-body">
                    <h5 class="card-title mb-3">
                      <i class="mdi mdi-shield-account me-2"></i>Additional Security Options
                    </h5>
                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <div class="border rounded p-3 h-100">
                          <h6 class="mb-2">
                            <i class="mdi mdi-two-factor-authentication text-primary me-2"></i>Two-Factor Authentication
                          </h6>
                          <p class="text-muted small mb-2">Add an extra layer of security to your account</p>
                          <button class="btn btn-sm btn-outline-primary" @click="manage2FA">
                            {{ userInfo.requireOTP === 'true' ? 'Manage 2FA' : 'Enable 2FA' }}
                          </button>
                        </div>
                      </div>
                      <div class="col-md-6 mb-3">
                        <div class="border rounded p-3 h-100">
                          <h6 class="mb-2">
                            <i class="mdi mdi-history text-info me-2"></i>Login History
                          </h6>
                          <p class="text-muted small mb-2">View your recent login activity</p>
                          <button class="btn btn-sm btn-outline-info" @click="viewLoginHistory">
                            View History
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import { authUrl, execute, toast } from "@/api";

export default {
  name: "ChangePassword",
  components: {
    NavigationBar
  },
  data() {
    return {
      loading: false,
      userInfo: {
        names: '',
        idNo: '',
        requireOTP: 'false',
        passwordExpiry: null
      },
      formData: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      errors: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      passwordChecks: {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
      }
    };
  },
  computed: {
    isFormValid() {
      return (
          this.formData.currentPassword &&
          this.formData.newPassword &&
          this.formData.confirmPassword &&
          this.formData.newPassword === this.formData.confirmPassword &&
          this.passwordStrength >= 3 &&
          !this.errors.currentPassword &&
          !this.errors.newPassword &&
          !this.errors.confirmPassword
      );
    },
    passwordStrength() {
      let strength = 0;
      if (this.passwordChecks.length) strength++;
      if (this.passwordChecks.uppercase) strength++;
      if (this.passwordChecks.lowercase) strength++;
      if (this.passwordChecks.number) strength++;
      if (this.passwordChecks.special) strength++;
      return strength;
    },
    passwordStrengthPercentage() {
      return (this.passwordStrength / 5) * 100;
    },
    passwordStrengthText() {
      if (this.passwordStrength === 0) return 'Very Weak';
      if (this.passwordStrength === 1) return 'Weak';
      if (this.passwordStrength === 2) return 'Fair';
      if (this.passwordStrength === 3) return 'Good';
      if (this.passwordStrength === 4) return 'Strong';
      return 'Very Strong';
    },
    passwordStrengthClass() {
      if (this.passwordStrength <= 1) return 'text-danger bg-danger';
      if (this.passwordStrength === 2) return 'text-warning bg-warning';
      if (this.passwordStrength === 3) return 'text-info bg-info';
      return 'text-success bg-success';
    },
    passwordExpiryDisplay() {
      if (!this.userInfo.passwordExpiry) return 'Not set';
      const expiryDate = new Date(this.userInfo.passwordExpiry);
      const today = new Date();
      const daysRemaining = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));

      if (daysRemaining < 0) {
        return 'Expired';
      } else if (daysRemaining === 0) {
        return 'Expires today';
      } else if (daysRemaining <= 7) {
        return `${daysRemaining} days remaining`;
      } else {
        return expiryDate.toLocaleDateString();
      }
    }
  },
  mounted() {
    this.loadUserInfo();
  },
  methods: {
    loadUserInfo() {
      try {
        const userInfoStr = sessionStorage.getItem('userInfo');
        if (userInfoStr) {
          this.userInfo = JSON.parse(userInfoStr);
        }
      } catch (error) {
        console.error('Error loading user info:', error);
        toast.error('Failed to load user information');
      }
    },

    validatePassword() {
      const password = this.formData.newPassword;

      // Update password checks
      this.passwordChecks.length = password.length >= 8;
      this.passwordChecks.uppercase = /[A-Z]/.test(password);
      this.passwordChecks.lowercase = /[a-z]/.test(password);
      this.passwordChecks.number = /[0-9]/.test(password);
      this.passwordChecks.special = /[!@#$%^&*(),.?":{}|<>]/.test(password);

      // Clear error if password meets all requirements
      if (this.passwordStrength >= 3) {
        this.errors.newPassword = '';
      } else if (password) {
        this.errors.newPassword = 'Password must meet the minimum requirements';
      }

      // Validate confirm password if it's been entered
      if (this.formData.confirmPassword) {
        this.validateConfirmPassword();
      }
    },

    validateConfirmPassword() {
      if (this.formData.newPassword !== this.formData.confirmPassword) {
        this.errors.confirmPassword = 'Passwords do not match';
      } else {
        this.errors.confirmPassword = '';
      }
    },

    resetForm() {
      this.formData = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
      this.errors = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
      this.showCurrentPassword = false;
      this.showNewPassword = false;
      this.showConfirmPassword = false;
      this.passwordChecks = {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
      };
    },

    async changePassword() {
      // Final validation
      if (!this.isFormValid) {
        toast.error('Please fill in all required fields correctly');
        return;
      }

      // Check if new password is same as current
      if (this.formData.currentPassword === this.formData.newPassword) {
        this.errors.newPassword = 'New password must be different from current password';
        toast.error('New password must be different from current password');
        return;
      }

      this.loading = true;

      const data = new FormData();
      data.append("function", "changePassword");
      data.append("currentPassword", btoa(this.formData.currentPassword));
      data.append("newPassword", btoa(this.formData.newPassword));
      data.append("idNo", this.userInfo.idNo);

      try {
        const res = await execute(data, authUrl);

        this.loading = false;

        if (res.data.success) {
          toast.success(res.data.message || 'Password changed successfully');

          // Reset form
          this.resetForm();

          toast.info('Please login with your new password');
          // Clear session and redirect to login immediately
          sessionStorage.clear();
          this.$router.push('/');
        } else {
          toast.error(res.data.message || 'Failed to change password');

          // Handle specific error for wrong current password
          if (res.data.message && res.data.message.includes('current password')) {
            this.errors.currentPassword = res.data.message;
          }
        }
      } catch (error) {
        this.loading = false;
        console.error('Error changing password:', error);
        const backendMessage = error?.response?.data?.message || error?.response?.data?.error;
        toast.error(backendMessage || error.message || 'An error occurred while changing password');
      }
    },

    manage2FA() {
      toast.info('2FA management coming soon');
      // TODO: Implement 2FA management
    },

    viewLoginHistory() {
      toast.info('Login history coming soon');
      // TODO: Implement login history view
    }
  }
};
</script>

<style scoped>
.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.progress-bar {
  transition: width 0.3s ease;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
}

.card-header {
  border-radius: 0.5rem 0.5rem 0 0 !important;
}

.input-group .btn-outline-secondary {
  border-color: #ced4da;
}

.input-group .btn-outline-secondary:hover {
  background-color: #f8f9fa;
  border-color: #ced4da;
  color: #6c757d;
}

.alert {
  border-radius: 0.5rem;
}

.border {
  border-radius: 0.5rem !important;
}

.avatar-sm {
  height: 3rem;
  width: 3rem;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .btn {
    font-size: 0.875rem;
  }
}
</style>